import { Badge } from '@/components/ui/badge';
import type { PaymentType } from '@/api/type/payment';

export type Status =
  // Membership status
  | 'active'
  | 'completed'
  | 'closed'
  | 'no_policy'
  // Loan eligibility
  | 'eligible'
  | 'not_eligible'
  // Payment status
  | 'paid'
  | 'unpaid'
  | 'no_dues'
  // Member status
  | 'alive'
  | 'deceased'
  // Payment type
  | 'cash'
  | 'online'
  | 'paid_by_pms';

const STATUS_LABEL: Record<Status, string> = {
  active: 'Active',
  completed: 'Completed',
  closed: 'Closed',
  no_policy: 'No Policy',
  eligible: 'Eligible',
  not_eligible: 'Not Eligible',
  paid: 'Paid',
  unpaid: 'Unpaid',
  no_dues: 'No Dues',
  alive: 'Alive',
  deceased: 'Deceased',
  cash: 'Cash',
  online: 'Online',
  paid_by_pms: 'Paid by PMS',
};

const STATUS_CLASS: Record<Status, string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  completed: 'border-[#4369a0]/35 bg-[#eef3f9] text-[#2d4f73]',
  closed: 'border-gray-300 bg-gray-100 text-gray-700',
  no_policy: 'border-amber-200 bg-amber-50 text-amber-900',
  eligible: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  not_eligible: 'border-gray-300 bg-gray-100 text-gray-700',
  paid: 'border-teal-200 bg-teal-50 text-teal-900',
  unpaid: 'border-amber-200 bg-amber-50 text-amber-900',
  no_dues: 'border-teal-200 bg-teal-50 text-teal-800',
  alive: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  deceased: 'border-gray-300 bg-gray-100 text-gray-700',
  cash: 'border-teal-200 bg-teal-50 text-teal-800',
  online: 'border-[#4369a0]/35 bg-[#eef3f9] text-[#2d4f73]',
  paid_by_pms: 'border-amber-200 bg-amber-50 text-amber-900',
};

const PAYMENT_TYPE_STATUS: Record<PaymentType, Status> = { Cash: 'cash', Online: 'online', PMS: 'paid_by_pms' };

/** Badge status for a payment type: Cash, Online or Paid by PMS. */
export const paymentTypeStatus = (type: PaymentType): Status => PAYMENT_TYPE_STATUS[type];

export const getStatusLabel = (status: Status): string => STATUS_LABEL[status];

export function StatusBadge({ status }: { status: Status }) {
  return (
    <Badge
      variant="outline"
      className={`${STATUS_CLASS[status]} h-6 rounded-md px-2 text-[11px] font-medium shadow-none`}
    >
      {STATUS_LABEL[status]}
    </Badge>
  );
}

const TEXT_BADGE_TONES = {
  teal: 'border-teal-200 bg-teal-50 text-teal-900',
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  brand: 'border-[#4369a0]/35 bg-[#eef3f9] text-[#2d4f73]',
  gray: 'border-gray-300 bg-gray-100 text-gray-700',
} as const;

/** Same look as StatusBadge, for free text such as "Paid for Oct". */
export function TextBadge({ tone, children }: { tone: keyof typeof TEXT_BADGE_TONES; children: string }) {
  return (
    <Badge
      variant="outline"
      className={`${TEXT_BADGE_TONES[tone]} h-6 shrink-0 rounded-md px-2 text-[11px] font-medium shadow-none`}
    >
      {children}
    </Badge>
  );
}
